import { useState, useCallback, useMemo } from 'react';
import { QueryGroup, QueryRule } from '../../types/QueryBuilder';
import { generateId, getFieldsByCategory, formatQueryDescription, isGroupValid } from '../../utils/queryBuilderUtils';
import { useQueryBuilderData } from '../../context/QueryBuilderDataContext';

export const useQueryBuilder = () => {
    const { fields, loadingFields, errorFields, categories } = useQueryBuilderData();

    const [groups, setGroups] = useState<QueryGroup[]>([{ id: generateId(), combinator: 'AND', rules: [] }]);

    const addRule = useCallback((groupId: string) => {
        const defaultCategory = categories[0] || '';
        const fieldsInCategory = getFieldsByCategory(fields, defaultCategory);
        const defaultField = fieldsInCategory[0]?.name || '';

        setGroups(prevGroups =>
            prevGroups.map(group =>
                group.id === groupId
                    ? {
                        ...group,
                        rules: [
                            ...group.rules,
                            {
                                id: generateId(),
                                category: defaultCategory,
                                field: defaultField,
                                operator: '=',
                                value: ''
                            }
                        ]
                    }
                    : group
            )
        );
    }, [categories, fields]);

    const removeRule = useCallback((groupId: string, ruleId: string) => {
        setGroups(prevGroups =>
            prevGroups.map(group =>
                group.id === groupId
                    ? {
                        ...group,
                        rules: group.rules.filter(rule => rule.id !== ruleId)
                    }
                    : group
            )
        );
    }, []);

    const updateRule = useCallback((groupId: string, ruleId: string, data: Partial<QueryRule>) => {
        setGroups(prevGroups =>
            prevGroups.map(group =>
                group.id === groupId
                    ? {
                        ...group,
                        rules: group.rules.map(rule =>
                            rule.id === ruleId ? { ...rule, ...data } : rule
                        )
                    }
                    : group
            )
        );
    }, []);

    const addGroup = useCallback(() => {
        if (groups.length < 2) {
            setGroups(prevGroups => [
                ...prevGroups,
                { id: generateId(), combinator: 'AND', rules: [] }
            ]);
        }
    }, [groups.length]);

    const removeGroup = useCallback((groupId: string) => {
        setGroups(prevGroups => prevGroups.filter(group => group.id !== groupId));
    }, []);

    const resetGroups = useCallback(() => {
        setGroups([{ id: generateId(), combinator: 'AND', rules: [] }]);
    }, []);

    const loadGroups = useCallback((newGroups: QueryGroup[]) => {
        setGroups(newGroups);
    }, []);

    const queryDescription = useMemo(() => {
        return formatQueryDescription(groups, fields);
    }, [groups, fields]);

    const isValid = useMemo(() => {
        return groups.length > 0 && groups.every(isGroupValid);
    }, [groups]);

    return {
        groups,
        categories,
        queryDescription,
        isValid,
        addRule,
        removeRule,
        updateRule,
        addGroup,
        removeGroup,
        resetGroups,
        loadGroups,
        fields,
        loadingFields,
        fieldsError: errorFields
    };
};
